import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Swal from 'sweetalert2';
import {
  Checkbox,
  Button,
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Snackbar,
  Chip,
  IconButton,
  Tooltip,
  Tab,
  Tabs,
  Card,
  CardContent,
  CardActions,
  Grid,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  Fab,
  useMediaQuery,
  useTheme,
  Divider,
  Avatar,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import PendingIcon from "@mui/icons-material/Pending";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import AssignmentIcon from "@mui/icons-material/Assignment";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import CloseIcon from "@mui/icons-material/Close";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PhoneIcon from "@mui/icons-material/Phone";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PersonIcon from "@mui/icons-material/Person";
import axios from "axios";

const VIEW_ONLY_SERVICE_NOS = ["8000598"];

const USER_IMG_BASE_URL = "https://esystems.cdl.lk/backendDGES/BizTrack/Home/DGESGetUserImg";

function getUserImgUrl(serviceNo) {
  if (!serviceNo) return null;
  return `${USER_IMG_BASE_URL}?serviceNo=${encodeURIComponent(serviceNo)}`;
}

function Attendance() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  const loggedInServiceNo = (localStorage.getItem('ServiceNo') || "").trim();
  const isViewOnly = VIEW_ONLY_SERVICE_NOS.includes(loggedInServiceNo);

  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [manualRecords, setManualRecords] = useState([]);
  const [locationRecords, setLocationRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [approving, setApproving] = useState(false);
  
  // FIX: Persist tab selection across refreshes using localStorage
  const [tabValue, setTabValue] = useState(() => {
    const savedTab = localStorage.getItem('attendanceTabValue');
    return savedTab ? parseInt(savedTab) : 0;
  });
  
  const [divisionHead, setDivisionHead] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [mobileViewMode, setMobileViewMode] = useState("card");

  // Tracks service numbers whose image failed to load, so we fall back
  // to the initials avatar instead of retrying a broken image forever.
  const [brokenImages, setBrokenImages] = useState({});

  const markImageBroken = (serviceNo) => {
    setBrokenImages((prev) => ({ ...prev, [serviceNo]: true }));
  };

  const [mapModal, setMapModal] = useState({
    open: false,
    lat: null,
    lng: null,
    name: "",
    serviceNo: "",
  });

  const openMapModal = (record) => {
    setMapModal({
      open: true,
      lat: record.Latitude,
      lng: record.Longitude,
      name: `${record.CED_FIRST_NAME} ${record.CED_LAST_NAME}`,
      serviceNo: record.CED_SERVICE_NO,
    });
  };

  const closeMapModal = () => {
    setMapModal({ open: false, lat: null, lng: null, name: "", serviceNo: "" });
  };

  const getAuthKey = () => {
    const token = localStorage.getItem("token");
    return token ? token.replace(/['"]+/g, '') : "";
  };

  const refreshPage = () => {
    window.location.reload();
  };

  const showConfirmationAlert = async (recordCount, isBulk = false, recordDetails = null, action = "approve") => {
    let title, text, confirmButtonText, confirmButtonColor;

    if (action === "reject") {
      if (isBulk) {
        title = 'Confirm Bulk Rejection';
        text = `Are you sure you want to reject ${recordCount} attendance record${recordCount > 1 ? 's' : ''}?`;
        confirmButtonText = `Yes, Reject ${recordCount} Record${recordCount > 1 ? 's' : ''}`;
        confirmButtonColor = '#d33';
      } else {
        const employeeName = recordDetails ? `${recordDetails.CED_FIRST_NAME} ${recordDetails.CED_LAST_NAME}` : 'this record';
        title = 'Confirm Rejection';
        text = `Are you sure you want to reject attendance for ${employeeName}?`;
        confirmButtonText = 'Yes, Reject';
        confirmButtonColor = '#d33';
      }
    } else {
      if (isBulk) {
        title = 'Confirm Bulk Approval';
        text = `Are you sure you want to approve ${recordCount} attendance record${recordCount > 1 ? 's' : ''}?`;
        confirmButtonText = `Yes, Approve ${recordCount} Record${recordCount > 1 ? 's' : ''}`;
        confirmButtonColor = '#3085d6';
      } else {
        const employeeName = recordDetails ? `${recordDetails.CED_FIRST_NAME} ${recordDetails.CED_LAST_NAME}` : 'this record';
        title = 'Confirm Approval';
        text = `Are you sure you want to approve attendance for ${employeeName}?`;
        confirmButtonText = 'Yes, Approve';
        confirmButtonColor = '#3085d6';
      }
    }

    const result = await Swal.fire({
      title,
      text,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor,
      cancelButtonColor: '#6c757d',
      confirmButtonText,
      cancelButtonText: 'Cancel',
      reverseButtons: true,
      background: '#fff',
      allowOutsideClick: false,
      allowEscapeKey: true,
    });

    return result.isConfirmed;
  };

  const showSuccessAlert = (message, isBulk = false, successCount = null, errorCount = null, shouldRefresh = true, action = "approve") => {
    if (isBulk && errorCount > 0) {
      Swal.fire({
        title: 'Partial Success',
        html: `
          <div style="text-align: left;">
            <p>✅ Successfully processed: <strong>${successCount}</strong> records</p>
            <p>❌ Failed: <strong>${errorCount}</strong> records</p>
            <p>${message}</p>
            <p style="margin-top: 10px;">Please check the records and try again for failed ones.</p>
          </div>
        `,
        icon: 'warning',
        confirmButtonColor: '#3085d6',
        confirmButtonText: 'OK',
      }).then((result) => {
        if (result.isConfirmed && shouldRefresh) refreshPage();
      });
    } else if (isBulk) {
      Swal.fire({
        title: 'Success!',
        text: message,
        icon: 'success',
        confirmButtonColor: '#3085d6',
        timer: 1500,
        timerProgressBar: true,
        showConfirmButton: true,
      }).then((result) => {
        if (result.isConfirmed || result.dismiss === Swal.DismissReason.timer) refreshPage();
      });
    } else {
      Swal.fire({
        title: action === "reject" ? 'Rejected!' : 'Approved!',
        text: message,
        icon: 'success',
        confirmButtonColor: '#3085d6',
        timer: 1500,
        timerProgressBar: true,
        showConfirmButton: true,
      }).then((result) => {
        if (result.isConfirmed || result.dismiss === Swal.DismissReason.timer) refreshPage();
      });
    }
  };

  const showErrorAlert = (message, error = null) => {
    Swal.fire({
      title: 'Error!',
      text: message,
      icon: 'error',
      confirmButtonColor: '#3085d6',
      confirmButtonText: 'OK',
      ...(error && { footer: `<span style="font-size: 12px;">${error}</span>` }),
    });
  };

  const showLoadingAlert = (message) => {
    Swal.fire({
      title: 'Processing',
      text: message,
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      willOpen: () => { Swal.showLoading(); },
    });
  };

  const fetchAttendanceRecords = async () => {
    setLoading(true);
    try {
      const authKey = getAuthKey();
      const sno = localStorage.getItem('ServiceNo');
      const response = await axios.get(
        `Attendance/GetDGESAttendancebyofficer?p_reporting_officer=${sno}`,
        {
          headers: {
            "auth-key": authKey,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.StatusCode === 200 && response.data.ResultSet) {
        const allRecords = response.data.ResultSet;

        if (allRecords.length > 0 && divisionHead === null) {
          setDivisionHead(allRecords[0].Division_head);
        }

        if (allRecords[0]?.Division_head === "1") {
          setManualRecords(allRecords.filter(r => r.HBL_STATUS === "P" || r.HBL_STATUS === "Q"));
          setLocationRecords(allRecords.filter(r => r.HBL_STATUS === "B" || r.HBL_STATUS === "C"));
        } else {
          setManualRecords(allRecords.filter(r => r.HBL_STATUS === "P"));
          setLocationRecords(allRecords.filter(r => r.HBL_STATUS === "B"));
        }
      }
    } catch (error) {
      console.error("Error fetching attendance:", error);
      showErrorAlert("Failed to fetch attendance records", error.message);
      setSnackbar({ open: true, message: "Failed to fetch attendance records", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAttendanceRecords();
  }, []);

  const getCurrentRecords = () => {
    let records = tabValue === 0 ? manualRecords : locationRecords;

    if (searchTerm) {
      records = records.filter(record =>
        record.CED_SERVICE_NO?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        `${record.CED_FIRST_NAME} ${record.CED_LAST_NAME}`?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        record.HBL_REASON?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== "all") {
      records = records.filter(record => record.HBL_STATUS === statusFilter);
    }

    return records;
  };

  const handleSelectAll = (event) => {
    const currentRecords = getCurrentRecords();
    if (event.target.checked) {
      setSelectedRecords(currentRecords.map((r) => r.HBL_ID));
    } else {
      setSelectedRecords([]);
    }
  };

  const handleSelectRecord = (recordId) => {
    setSelectedRecords((prev) =>
      prev.includes(recordId) ? prev.filter((id) => id !== recordId) : [...prev, recordId]
    );
  };

  const updateAttendanceStatus = async (record, newStatus) => {
    return axios.put(
      `Attendance/DGESUpdateAttendancestatus`,
      {
        p_ID: record.HBL_ID,
        p_barcode_no: record.HBL_BARCODE_NO,
        p_hstatus: newStatus,
      },
      {
        headers: {
          "Content-Type": "application/json",
          "auth-key": getAuthKey(),
        },
      }
    );
  };

  const sendRejectionSms = async (employeeMobileNo, attendanceDate) => {
    const phoneNo = employeeMobileNo?.trim();
    if (!phoneNo) {
      throw new Error("No mobile number available for this employee.");
    }

    const smsUrl = new URL("https://dgessmsgateway.dockyardsoftware.com/SMS/SendCmnsms");
    smsUrl.searchParams.set("P_Contact", phoneNo);
    smsUrl.searchParams.set(
      "P_Message",
      `Your attendance request for ${formatDate(attendanceDate)} has been rejected. Please contact your reporting officer for assistance.`
    );

    const response = await fetch(smsUrl.toString());
    if (!response.ok) {
      throw new Error(`SMS gateway request failed (HTTP ${response.status}).`);
    }
  };

  const handleApproveSelected = async () => {
    if (isViewOnly) return;
    if (selectedRecords.length === 0) { showErrorAlert("Please select at least one record to approve"); return; }
    const confirmed = await showConfirmationAlert(selectedRecords.length, true, null, "approve");
    if (!confirmed) return;

    setApproving(true);
    showLoadingAlert(`Approving ${selectedRecords.length} record(s)...`);

    let successCount = 0, errorCount = 0;
    const currentRecords = getCurrentRecords();
    const recordsToApprove = currentRecords.filter((r) => selectedRecords.includes(r.HBL_ID));

    for (const record of recordsToApprove) {
      try {
        let newStatus = divisionHead === "1" ? "I"
          : record.HBL_STATUS === "B" ? "C"
          : record.HBL_STATUS === "P" ? "Q"
          : "I";

        const response = await updateAttendanceStatus(record, newStatus);
        if (response.data.StatusCode === 200) successCount++;
        else errorCount++;
      } catch { errorCount++; }
    }

    Swal.close();
    if (successCount > 0) {
      showSuccessAlert(`Successfully approved ${successCount} record(s)`, true, successCount, errorCount, true, "approve");
    } else {
      showErrorAlert("Failed to approve records. Please try again.");
    }
    setSelectedRecords([]);
    setApproving(false);
  };

  const handleRejectSelected = async () => {
    if (isViewOnly) return;
    if (selectedRecords.length === 0) { showErrorAlert("Please select at least one record to reject"); return; }
    const confirmed = await showConfirmationAlert(selectedRecords.length, true, null, "reject");
    if (!confirmed) return;

    setApproving(true);
    showLoadingAlert(`Rejecting ${selectedRecords.length} record(s)...`);

    let successCount = 0, errorCount = 0;
    let smsErrorCount = 0;
    const currentRecords = getCurrentRecords();
    const recordsToReject = currentRecords.filter((r) => selectedRecords.includes(r.HBL_ID));

    for (const record of recordsToReject) {
      try {
        const response = await updateAttendanceStatus(record, "N");
        if (response.data.StatusCode === 200) {
          successCount++;
          try {
            await sendRejectionSms(record.Mobile_No, record.HBL_BARCODE_DATE);
          } catch (smsError) {
            smsErrorCount++;
            console.error(`Failed to send rejection SMS for attendance ${record.HBL_ID}:`, smsError);
          }
        } else errorCount++;
      } catch { errorCount++; }
    }

    Swal.close();
    if (successCount > 0) {
      const smsMessage = smsErrorCount
        ? `SMS notification could not be sent for ${smsErrorCount} rejected record(s).`
        : "SMS notifications sent for all rejected records.";
      showSuccessAlert(`Successfully rejected ${successCount} record(s). ${smsMessage}`, true, successCount, errorCount, true, "reject");
    } else {
      showErrorAlert("Failed to reject records. Please try again.");
    }
    setSelectedRecords([]);
    setApproving(false);
  };

  const handleApproveSingle = async (record) => {
    if (isViewOnly) return;
    const confirmed = await showConfirmationAlert(1, false, record, "approve");
    if (!confirmed) return;

    setApproving(true);
    showLoadingAlert(`Processing approval for ${record.CED_FIRST_NAME} ${record.CED_LAST_NAME}...`);

    try {
      const newStatus = divisionHead === "1" ? "I"
        : record.HBL_STATUS === "B" ? "C"
        : record.HBL_STATUS === "P" ? "Q"
        : "I";

      const response = await updateAttendanceStatus(record, newStatus);
      Swal.close();

      if (response.data.StatusCode === 200) {
        showSuccessAlert(
          `Successfully approved attendance for ${record.CED_FIRST_NAME} ${record.CED_LAST_NAME}`,
          false, null, null, true, "approve"
        );
        setSelectedRecords([]);
      } else {
        showErrorAlert("Failed to approve attendance", response.data.Message || "Unknown error occurred");
        setApproving(false);
      }
    } catch (error) {
      Swal.close();
      showErrorAlert("Error occurred while approving", error.message);
      setApproving(false);
    }
  };

  const handleRejectSingle = async (record) => {
    if (isViewOnly) return;
    const confirmed = await showConfirmationAlert(1, false, record, "reject");
    if (!confirmed) return;

    setApproving(true);
    showLoadingAlert(`Processing rejection for ${record.CED_FIRST_NAME} ${record.CED_LAST_NAME}...`);

    try {
      const response = await updateAttendanceStatus(record, "N");
      Swal.close();

      if (response.data.StatusCode === 200) {
        let smsMessage = "SMS notification sent.";
        try {
          await sendRejectionSms(record.Mobile_No, record.HBL_BARCODE_DATE);
        } catch (smsError) {
          console.error(`Failed to send rejection SMS for attendance ${record.HBL_ID}:`, smsError);
          smsMessage = `SMS notification could not be sent: ${smsError.message}`;
        }

        showSuccessAlert(
          `Successfully rejected attendance for ${record.CED_FIRST_NAME} ${record.CED_LAST_NAME}. ${smsMessage}`,
          false, null, null, true, "reject"
        );
        setSelectedRecords([]);
      } else {
        showErrorAlert("Failed to reject attendance", response.data.Message || "Unknown error occurred");
        setApproving(false);
      }
    } catch (error) {
      Swal.close();
      showErrorAlert("Error occurred while rejecting", error.message);
      setApproving(false);
    }
  };

  const handleCallEmployee = (record) => {
    const mobileNo = record.Mobile_No;
    if (!mobileNo) {
      showErrorAlert("No mobile number available for this employee.");
      return;
    }
    const cleaned = mobileNo.replace(/\s+/g, "");

    Swal.fire({
      title: `<span style="font-size:16px">${record.CED_FIRST_NAME} ${record.CED_LAST_NAME}</span>`,
      html: `
        <div style="font-size: 22px; font-weight: bold; color: #1565c0; margin: 8px 0;">
          📞 ${mobileNo}
        </div>
      `,
      showDenyButton: true,
      showCancelButton: true,
      confirmButtonText: '📞 Call',
      denyButtonText: '💬 Message',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#2e7d32',
      denyButtonColor: '#1565c0',
      cancelButtonColor: '#6c757d',
      reverseButtons: false,
    }).then((result) => {
      if (result.isConfirmed) {
        window.location.href = `tel:${cleaned}`;
      } else if (result.isDenied) {
        window.location.href = `sms:${cleaned}`;
      }
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    if (isMobile) {
      return date.toLocaleDateString() + " " + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleString();
  };

  const getStatusChip = (status) => {
    const chipProps = {
      size: "small",
      sx: { fontSize: isMobile ? '0.7rem' : '0.75rem' },
    };
    switch (status) {
      case "P": return <Chip label="Manual"   color="warning" icon={<PendingIcon />}    {...chipProps} />;
      case "Q": return <Chip label="Level 2"  color="info"    icon={<PendingIcon />}    {...chipProps} />;
      case "C": return <Chip label="Level 2"  color="info"    icon={<LocationOnIcon />} {...chipProps} />;
      case "I": return <Chip label="Approved" color="success" icon={<CheckCircleIcon />}{...chipProps} />;
      case "N": return <Chip label="Rejected" color="error"   icon={<CancelIcon />}     {...chipProps} />;
      default:  return <Chip label={status} {...chipProps} />;
    }
  };

  const getActionButtonText = (record) => {
    if (divisionHead === "1") return "Approve";
    if (record.HBL_STATUS === "P") return "Send to Q";
    if (record.HBL_STATUS === "B") return "Send to C";
    return "Approve";
  };

  // ── Employee profile image (fetched via DGESGetUserImg by service no) ──────
  const EmployeeAvatar = ({ record, size = 40 }) => {
    const serviceNo = record.CED_SERVICE_NO;
    const initials = `${record.CED_FIRST_NAME?.[0] || ""}${record.CED_LAST_NAME?.[0] || ""}`.toUpperCase();
    const imgUrl = getUserImgUrl(serviceNo);
    const showImage = imgUrl && !brokenImages[serviceNo];

    return (
      <Avatar
        src={showImage ? imgUrl : undefined}
        alt={`${record.CED_FIRST_NAME} ${record.CED_LAST_NAME}`}
        onError={() => markImageBroken(serviceNo)}
        sx={{
          width: size,
          height: size,
          bgcolor: '#1976d2',
          fontSize: size * 0.4,
          fontWeight: 600,
        }}
      >
        {!showImage && (initials || <PersonIcon fontSize="small" />)}
      </Avatar>
    );
  };

  const LocationChip = ({ record }) => {
    const hasLocation = record.Latitude && record.Longitude;
    if (!hasLocation) return null;

    return (
      <Chip
        label="Location"
        icon={<LocationOnIcon />}
        size="small"
        onClick={() => openMapModal(record)}
        sx={{
          fontSize: isMobile ? '0.7rem' : '0.75rem',
          cursor: 'pointer',
          bgcolor: '#e65100',
          color: 'white',
          '& .MuiChip-icon': { color: 'white' },
          '&:hover': { bgcolor: '#bf360c' },
          transition: 'background-color 0.2s',
        }}
      />
    );
  };

  const PhoneChip = ({ record }) => {
    if (!record.Mobile_No) return null;
    return (
      <Chip
        label={record.Mobile_No}
        icon={<PhoneIcon />}
        size="small"
        onClick={() => handleCallEmployee(record)}
        sx={{
          fontSize: isMobile ? '0.7rem' : '0.75rem',
          cursor: 'pointer',
          bgcolor: '#1565c0',
          color: 'white',
          '& .MuiChip-icon': { color: 'white' },
          '&:hover': { bgcolor: '#0d47a1' },
          transition: 'background-color 0.2s',
        }}
      />
    );
  };

  const MapModal = () => {
    if (!mapModal.open) return null;

    const mapSrc = `https://www.google.com/maps?q=${mapModal.lat},${mapModal.lng}&z=16&output=embed`;
    const mapsUrl = `https://www.google.com/maps?q=${mapModal.lat},${mapModal.lng}`;
    const modalImgUrl = getUserImgUrl(mapModal.serviceNo);
    const modalImgBroken = brokenImages[mapModal.serviceNo];

    return (
      <Box
        onClick={closeMapModal}
        sx={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          bgcolor: 'rgba(0,0,0,0.65)',
          zIndex: 2000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
        }}
      >
        <Paper
          onClick={(e) => e.stopPropagation()}
          elevation={12}
          sx={{
            width: '100%',
            maxWidth: 620,
            borderRadius: 2,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box sx={{
            px: 2, py: 1.5,
            bgcolor: '#1565c0',
            color: 'white',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Avatar
                src={!modalImgBroken ? modalImgUrl : undefined}
                alt={mapModal.name}
                onError={() => markImageBroken(mapModal.serviceNo)}
                sx={{ width: 40, height: 40, bgcolor: 'rgba(255,255,255,0.2)' }}
              >
                {modalImgBroken && <PersonIcon />}
              </Avatar>
              <Box>
                <Typography variant="subtitle1" fontWeight="bold" lineHeight={1.2}>
                  {mapModal.name}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.85 }}>
                  Service No: {mapModal.serviceNo}
                </Typography>
              </Box>
            </Box>
            <IconButton size="small" onClick={closeMapModal} sx={{ color: 'white' }}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{
            px: 2, py: 0.75,
            bgcolor: '#e3f2fd',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            borderBottom: '1px solid #bbdefb',
          }}>
            <LocationOnIcon fontSize="small" sx={{ color: '#1565c0' }} />
            <Typography variant="caption" color="text.secondary">
              Latitude: <strong>{mapModal.lat}</strong> &nbsp;|&nbsp; Longitude: <strong>{mapModal.lng}</strong>
            </Typography>
          </Box>

          <Box sx={{ height: isMobile ? 280 : 400, width: '100%' }}>
            <iframe
              title="Employee Attendance Location"
              width="100%"
              height="100%"
              frameBorder="0"
              style={{ display: 'block' }}
              src={mapSrc}
              allowFullScreen
            />
          </Box>

          <Box sx={{
            px: 2, py: 1.5,
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 1,
            bgcolor: '#fafafa',
            borderTop: '1px solid #e0e0e0',
          }}>
            <Button
              variant="outlined"
              size="small"
              onClick={closeMapModal}
              sx={{ textTransform: 'none' }}
            >
              Close
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<OpenInNewIcon />}
              onClick={() => window.open(mapsUrl, '_blank')}
              sx={{ textTransform: 'none', bgcolor: '#1565c0', '&:hover': { bgcolor: '#0d47a1' } }}
            >
              Open in Google Maps
            </Button>
          </Box>
        </Paper>
      </Box>
    );
  };

  // ── Mobile card ────────────────────────────────────────────────────────────
  const MobileAttendanceCard = ({ record }) => (
    <Card
      sx={{
        mb: 2,
        position: 'relative',
        borderLeft: selectedRecords.includes(record.HBL_ID) ? '4px solid #1976d2' : 'none',
        bgcolor: selectedRecords.includes(record.HBL_ID) ? '#f0f7ff' : 'white',
      }}
      elevation={1}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Hide checkbox for view-only users */}
            {!isViewOnly && (
              <Checkbox
                checked={selectedRecords.includes(record.HBL_ID)}
                onChange={() => handleSelectRecord(record.HBL_ID)}
                size="small"
                sx={{ p: 0.5 }}
              />
            )}
            <EmployeeAvatar record={record} size={36} />
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
              {record.CED_SERVICE_NO}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <LocationChip record={record} />
            <PhoneChip record={record} />
          </Box>
        </Box>

        <Divider sx={{ my: 1 }} />

        <Grid container spacing={1.5}>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">Employee Name</Typography>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {record.CED_FIRST_NAME} {record.CED_LAST_NAME}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">Date & Time</Typography>
            <Typography variant="body2">{formatDate(record.HBL_BARCODE_DATE)}</Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary">Reason</Typography>
            <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
              {record.HBL_REASON || 'N/A'}
            </Typography>
          </Grid>
        </Grid>
      </CardContent>

      {/* Show action buttons only for non-view-only users */}
      {!isViewOnly ? (
        <CardActions sx={{ p: 1.5, pt: 0, gap: 1 }}>
          <Button
            variant="contained"
            size="small"
            fullWidth
            onClick={() => handleApproveSingle(record)}
            disabled={approving}
            startIcon={<CheckCircleIcon sx={{ fontSize: 20 }} />}
            sx={{
              textTransform: 'none',
              py: 0.75,
              backgroundColor: '#2e7d32',
              '&:hover': { backgroundColor: '#1b5e20' },
            }}
          >
            {getActionButtonText(record)}
          </Button>

          <Button
            variant="contained"
            size="small"
            fullWidth
            onClick={() => handleRejectSingle(record)}
            disabled={approving}
            startIcon={<CancelIcon sx={{ fontSize: 20 }} />}
            sx={{
              textTransform: 'none',
              py: 0.75,
              backgroundColor: '#d32f2f',
              '&:hover': { backgroundColor: '#b71c1c' },
            }}
          >
            Reject
          </Button>
        </CardActions>
      ) : (
        /* View-only badge for mobile */
        <CardActions sx={{ p: 1.5, pt: 0 }}>
          <Chip
            icon={<VisibilityIcon />}
            label="View Only"
            size="small"
            sx={{
              bgcolor: '#f3e5f5',
              color: '#6a1b9a',
              '& .MuiChip-icon': { color: '#6a1b9a' },
              fontWeight: 500,
            }}
          />
        </CardActions>
      )}
    </Card>
  );

  const SearchFilterBar = () => (
    <Box sx={{ display: 'flex', gap: 1, mb: 2, flexDirection: isMobile ? 'column' : 'row' }}>
      <Menu
        anchorEl={filterAnchorEl}
        open={Boolean(filterAnchorEl)}
        onClose={() => setFilterAnchorEl(null)}
      >
        <MenuItem onClick={() => { setStatusFilter("all"); setFilterAnchorEl(null); }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {statusFilter === "all" && <CheckBoxIcon fontSize="small" color="primary" />}
            All Status
          </Box>
        </MenuItem>
        <MenuItem onClick={() => { setStatusFilter("P"); setFilterAnchorEl(null); }}>Pending - Manual</MenuItem>
        <MenuItem onClick={() => { setStatusFilter("B"); setFilterAnchorEl(null); }}>Pending - Location</MenuItem>
        <MenuItem onClick={() => { setStatusFilter("Q"); setFilterAnchorEl(null); }}>Level 2 (Manual)</MenuItem>
        <MenuItem onClick={() => { setStatusFilter("C"); setFilterAnchorEl(null); }}>Level 2 (Location)</MenuItem>
      </Menu>
    </Box>
  );

  const BulkActionBar = ({ selectedCount }) => {
    if (selectedCount === 0 || isViewOnly) return null;
    return (
      <Paper
        elevation={3}
        sx={{
          position: 'fixed',
          bottom: 16, left: 16, right: 16,
          zIndex: 1000,
          p: 1.5,
          bgcolor: '#1976d2',
          color: 'white',
          borderRadius: 2,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="body2" fontWeight="bold">{selectedCount} selected</Typography>
          <IconButton size="small" onClick={() => setSelectedRecords([])} sx={{ color: 'white' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained" color="success" fullWidth size="small"
            onClick={handleApproveSelected} disabled={approving}
            sx={{ bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' } }}
          >
            Approve All
          </Button>
          <Button
            variant="contained" color="error" fullWidth size="small"
            onClick={handleRejectSelected} disabled={approving}
          >
            Reject All
          </Button>
        </Box>
      </Paper>
    );
  };

  const currentRecords = getCurrentRecords();
  const selectedCount = selectedRecords.length;

  return (
    <Box sx={{
      width: "100%",
      maxWidth: "1200px",
      margin: "0 auto",
      padding: isMobile ? 1 : 2,
      pb: selectedCount > 0 && isMobile ? 10 : 2,
    }}>
      <MapModal />

      {/* Header */}
      <Paper elevation={3} sx={{ p: isMobile ? 1.5 : 2, mb: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Typography variant={isMobile ? "h6" : "h5"} sx={{ fontWeight: "bold", color: "#1976d2" }}>
              Attendance Approvals
            </Typography>
            {/* View-only banner in header */}
            {isViewOnly && (
              <Chip
                icon={<VisibilityIcon />}
                label="View Only"
                size="small"
                sx={{
                  bgcolor: '#f3e5f5',
                  color: '#6a1b9a',
                  '& .MuiChip-icon': { color: '#6a1b9a' },
                  fontWeight: 600,
                }}
              />
            )}
          </Box>
          <Button
            variant="outlined" color="primary"
            onClick={() => navigate(-1)}
            size={isMobile ? "small" : "medium"}
            sx={{ textTransform: "none" }}
          >
            Back
          </Button>
        </Box>
      </Paper>

      {/* Tab indicator showing current tab name and record count */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, px: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {tabValue === 0 ? '' : ''}
          {currentRecords.length > 0 && ` (${currentRecords.length} records)`}
        </Typography>
      </Box>

      {/* Tabs */}
      <Paper elevation={2} sx={{ mb: 2 }}>
        <Tabs
          value={tabValue}
          onChange={(e, newValue) => {
            setTabValue(newValue);
            // Save tab selection to localStorage
            localStorage.setItem('attendanceTabValue', newValue.toString());
            setSelectedRecords([]);
            setSearchTerm("");
          }}
          indicatorColor="primary"
          textColor="primary"
          variant={isMobile ? "fullWidth" : "standard"}
          centered={isMobile}
        >
          <Tab
            icon={<AssignmentIcon />}
            label={!isMobile ? "Manual Attendance" : "Manual"}
            iconPosition="start"
            sx={{ textTransform: "none", fontWeight: "bold", minHeight: 48 }}
          />
          <Tab
            icon={<LocationOnIcon />}
            label={!isMobile ? "Location Attendance" : "Location"}
            iconPosition="start"
            sx={{ textTransform: "none", fontWeight: "bold", minHeight: 48 }}
          />
        </Tabs>
      </Paper>

      {currentRecords.length > 0 && <SearchFilterBar />}

      {/* Desktop bulk-action bar — hidden for view-only */}
      {currentRecords.length > 0 && !isMobile && !isViewOnly && (
        <Paper elevation={2} sx={{ p: 1.5, mb: 2, bgcolor: "#f5f5f5" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
            <Typography variant="body1">
              Selected: <strong>{selectedRecords.length}</strong> of {currentRecords.length} records
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="outlined" color="error" size="small"
                onClick={() => setSelectedRecords([])}
                disabled={selectedRecords.length === 0}
                sx={{ textTransform: "none" }}
              >
                Clear
              </Button>
              <Button
                variant="contained" color="success" size="small"
                onClick={handleApproveSelected}
                disabled={approving || selectedRecords.length === 0}
                sx={{ textTransform: "none" }}
              >
                {approving ? <CircularProgress size={20} /> : "Approve Selected"}
              </Button>
              <Button
                variant="contained" color="error" size="small"
                onClick={handleRejectSelected}
                disabled={approving || selectedRecords.length === 0}
                sx={{ textTransform: "none" }}
              >
                {approving ? <CircularProgress size={20} /> : "Reject Selected"}
              </Button>
            </Box>
          </Box>
        </Paper>
      )}

      {/* Main content */}
      <Paper elevation={3}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
            <CircularProgress />
          </Box>
        ) : currentRecords.length > 0 ? (
          <>
            {/* Mobile card view */}
            {isMobile && (
              <Box sx={{ p: 1 }}>
                {currentRecords.map((record) => (
                  <MobileAttendanceCard key={record.HBL_ID} record={record} />
                ))}
              </Box>
            )}

            {/* Desktop table view */}
            {!isMobile && (
              <Box sx={{ overflowX: "auto" }}>
                <Table>
                  <TableHead sx={{ bgcolor: "#1976d2" }}>
                    <TableRow>
                      {/* Hide select-all checkbox for view-only */}
                      {!isViewOnly && (
                        <TableCell padding="checkbox" sx={{ color: "white" }}>
                          <Checkbox
                            checked={selectedRecords.length === currentRecords.length && currentRecords.length > 0}
                            onChange={handleSelectAll}
                            sx={{ color: "white" }}
                          />
                        </TableCell>
                      )}
                      <TableCell sx={{ color: "white", fontWeight: "bold" }}>Photo</TableCell>
                      <TableCell sx={{ color: "white", fontWeight: "bold" }}>Service No</TableCell>
                      <TableCell sx={{ color: "white", fontWeight: "bold" }}>Reason</TableCell>
                      <TableCell sx={{ color: "white", fontWeight: "bold" }}>Employee Name</TableCell>
                      <TableCell sx={{ color: "white", fontWeight: "bold" }}>Date & Time</TableCell>
                      <TableCell sx={{ color: "white", fontWeight: "bold" }}>Location</TableCell>
                      <TableCell sx={{ color: "white", fontWeight: "bold" }}>Mobile</TableCell>
                      <TableCell sx={{ color: "white", fontWeight: "bold" }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {currentRecords.map((record) => (
                      <TableRow
                        key={record.HBL_ID}
                        hover
                        selected={selectedRecords.includes(record.HBL_ID)}
                        sx={{ "&:hover": { bgcolor: "#f5f5f5" } }}
                      >
                        {/* Hide per-row checkbox for view-only */}
                        {!isViewOnly && (
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={selectedRecords.includes(record.HBL_ID)}
                              onChange={() => handleSelectRecord(record.HBL_ID)}
                            />
                          </TableCell>
                        )}
                        <TableCell>
                          <EmployeeAvatar record={record} size={40} />
                        </TableCell>
                        <TableCell>{record.CED_SERVICE_NO}</TableCell>
                        <TableCell sx={{ maxWidth: 200 }}>{record.HBL_REASON}</TableCell>
                        <TableCell>{record.CED_FIRST_NAME} {record.CED_LAST_NAME}</TableCell>
                        <TableCell>{formatDate(record.HBL_BARCODE_DATE)}</TableCell>
                        <TableCell>
                          <LocationChip record={record} />
                        </TableCell>

                        <TableCell>
                          {record.Mobile_No ? (
                            <Tooltip title={`Contact ${record.Mobile_No}`}>
                              <IconButton
                                size="small"
                                onClick={() => handleCallEmployee(record)}
                                sx={{
                                  color: 'white',
                                  bgcolor: '#1565c0',
                                  '&:hover': { bgcolor: '#0d47a1' },
                                  width: 32,
                                  height: 32,
                                }}
                              >
                                <PhoneIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Typography variant="caption" color="text.disabled">—</Typography>
                          )}
                        </TableCell>

                        {/* Actions column: approve/reject for normal users, View Only chip for restricted */}
                        <TableCell>
                          {!isViewOnly ? (
                            <Box sx={{ display: "flex", gap: 0.5 }}>
                              <Tooltip title={getActionButtonText(record)}>
                                <IconButton
                                  color="success"
                                  onClick={() => handleApproveSingle(record)}
                                  disabled={approving}
                                  size="small"
                                >
                                  <CheckCircleIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Reject">
                                <IconButton
                                  color="error"
                                  onClick={() => handleRejectSingle(record)}
                                  disabled={approving}
                                  size="small"
                                >
                                  <CancelIcon />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          ) : (
                            <Chip
                              icon={<VisibilityIcon />}
                              label="View Only"
                              size="small"
                              sx={{
                                bgcolor: '#f3e5f5',
                                color: '#6a1b9a',
                                '& .MuiChip-icon': { color: '#6a1b9a' },
                                fontSize: '0.7rem',
                              }}
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
          </>
        ) : (
          <Box sx={{ textAlign: "center", p: 5 }}>
            <Typography variant={isMobile ? "subtitle1" : "h6"} color="text.secondary">
              No pending {tabValue === 0 ? "manual" : "location"} attendance records found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {searchTerm ? "Try adjusting your search or filter criteria" : "All records have been processed"}
            </Typography>
            {searchTerm && (
              <Button size="small" onClick={() => setSearchTerm("")} sx={{ mt: 2 }}>
                Clear Search
              </Button>
            )}
          </Box>
        )}
      </Paper>

      {/* Mobile bulk action bar — hidden for view-only */}
      {isMobile && selectedCount > 0 && !isViewOnly && (
        <BulkActionBar selectedCount={selectedCount} totalCount={currentRecords.length} />
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Attendance;